/**
 * PricingEngagement — enterprise engagement & value-based pricing dialog.
 *
 * Ontoligent's paid tiers are negotiated (Palantir-style), not self-served. This
 * is the premium entry point: a two-pane dialog with a branded value panel and a
 * structured discovery form. It produces a directional investment range that
 * anchors the conversation, and submits via the resilient contact chain
 * (Django → EmailJS → mailto) so the request is never lost.
 *
 * Design intent: feel like a hand-built enterprise sales surface — generous
 * spacing, restrained palette, sectioned form, no "generated popup" clutter.
 */
import { useMemo, useState } from "react";
import {
  X, Server, Cloud, KeyRound, ShieldCheck, ArrowRight, Loader2,
  Check, Lock, UserCheck, Building2,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { submitContact, type ContactResult } from "@/lib/contact";

type Plan = "pro" | "enterprise";
type Hosting = "self_hosted" | "redpill_hosted";
type Llm = "byo" | "managed";
type Volume = "small" | "medium" | "large" | "xlarge";

interface Props {
  open: boolean;
  plan: Plan;
  orgName?: string;
  industry?: string;
  contactEmail?: string;
  /** Prefills the "what decision or workflow" field — used when the dialog is
   *  opened from a specific feature's paywall (e.g. the Semantic Composer)
   *  so the discovery form arrives with context instead of a blank slate. */
  initialUseCase?: string;
  onClose: () => void;
}

const INDUSTRIES = [
  "insurance", "healthcare", "pharma", "finance", "legal", "tax",
  "manufacturing", "retail", "energy", "telecom", "public_sector",
  "logistics", "real_estate", "education", "other",
];

const COMPLIANCE = [
  { key: "hipaa", label: "HIPAA" },
  { key: "gdpr", label: "GDPR" },
  { key: "pci", label: "PCI-DSS" },
  { key: "sox", label: "SOX" },
  { key: "offline", label: "Offline / zero-egress" },
];

const VOLUME_LABEL: Record<Volume, string> = {
  small: "< 100k records · < 1 TB",
  medium: "100k–1M records · 1–5 TB",
  large: "1M–10M records · 5–50 TB",
  xlarge: "> 10M records · > 50 TB",
};

// --- Directional estimate (monthly USD, before ±range). Anchors negotiation. --
function estimate(opts: {
  plan: Plan; hosting: Hosting; llm: Llm; volume: Volume;
  workflows: number; compliance: string[];
}) {
  const tierBase = opts.plan === "enterprise" ? 6000 : 1800;
  const hostingUplift = opts.hosting === "redpill_hosted" ? tierBase * 0.35 : 0;
  const llmManaged = opts.llm === "managed" ? 1200 : 0;
  const dataComponent = { small: 400, medium: 1500, large: 4500, xlarge: 12000 }[opts.volume];
  const perWorkflow = opts.plan === "enterprise" ? 900 : 600;
  const workflowComponent = Math.max(0, opts.workflows) * perWorkflow;
  const regimePremium: Record<string, number> = {
    hipaa: 1200, gdpr: 800, pci: 1000, sox: 700, offline: 2500,
  };
  const complianceUplift = opts.compliance.reduce((s, k) => s + (regimePremium[k] ?? 0), 0);
  const consultant = opts.plan === "enterprise" ? 3500 : 900;

  const monthly = tierBase + hostingUplift + llmManaged + dataComponent
    + workflowComponent + complianceUplift + consultant;
  const annual = monthly * 12;
  return {
    annualLow: Math.round((annual * 0.65) / 1000) * 1000,
    annualHigh: Math.round((annual * 1.35) / 1000) * 1000,
  };
}

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 mt-5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted first:mt-0">
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-medium text-text/80">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-lg border border-border bg-bg/60 px-3 py-2 text-sm text-text " +
  "outline-none transition focus:border-accent/60 focus:ring-2 focus:ring-accent/15";

export function PricingEngagement({ open, plan, orgName, industry, contactEmail, initialUseCase, onClose }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState(contactEmail ?? "");
  const [company, setCompany] = useState(orgName ?? "");
  const [ind, setInd] = useState(industry ?? "insurance");
  const [useCase, setUseCase] = useState(initialUseCase ?? "");
  const [hosting, setHosting] = useState<Hosting>("redpill_hosted");
  const [llm, setLlm] = useState<Llm>("managed");
  const [volume, setVolume] = useState<Volume>("medium");
  const [workflows, setWorkflows] = useState(3);
  const [compliance, setCompliance] = useState<string[]>([]);
  const [timeline, setTimeline] = useState("this_quarter");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<ContactResult | null>(null);

  const est = useMemo(
    () => estimate({ plan, hosting, llm, volume, workflows, compliance }),
    [plan, hosting, llm, volume, workflows, compliance],
  );

  const recommended = workflows <= 2 || volume === "small"
    ? "Begin with a 2-week paid pilot on one high-value use case."
    : "Knowledge Assessment, then a phased rollout with outcome-linked milestones.";

  // Consultative / Palantir-style: pricing is never shown as a sticker in the
  // dialog. Both Pro and Enterprise are negotiated — the buyer sees engagement
  // scope, and a Redpill architect confirms the figure in the follow-up. The
  // indicative range is computed only as an internal lead qualifier (sent in
  // the notification meta), never surfaced to the buyer.
  const SCOPE = plan === "enterprise"
    ? ["Knowledge architecture", "Ontology engineering", "Hybrid + graph retrieval",
       "Governance & residency", "SLA-backed support", "Quarterly value reviews"]
    : ["Knowledge architecture", "Ontology modeling", "Hybrid retrieval",
       "Production deployment", "Architecture review", "Priority onboarding"];

  const planValue = plan === "enterprise"
    ? [
        "Named solution architect with scheduled reviews and an SLA",
        "Graph + hybrid retrieval, co-built acceptance & eval suite",
        "Custom governance policy, data residency, offline / zero-egress build",
        "Quarterly value review tied to your success metric",
      ]
    : [
        "Architecture review by a Redpill engineer on each blueprint",
        "Hybrid retrieval with expanded evaluation gates",
        "Approval gates and data-residency options",
        "Priority onboarding and a named point of contact",
      ];

  if (!open) return null;

  function toggleCompliance(k: string) {
    setCompliance((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    const res = await submitContact({
      kind: "engagement",
      name,
      email,
      company,
      subject: `Ontoligent ${plan} engagement — ${company || "new"}`,
      message: useCase,
      meta: {
        plan,
        industry: ind,
        hosting: hosting === "redpill_hosted" ? "We host" : "Self-hosted",
        llm: llm === "managed" ? "Managed by Redpill" : "Bring-your-own key",
        data_volume: VOLUME_LABEL[volume],
        production_workflows: workflows,
        compliance: compliance.length ? compliance : "none specified",
        timeline,
        indicative_range: `${usd(est.annualLow)} – ${usd(est.annualHigh)} / year`,
        recommended_path: recommended,
      },
    });
    setResult(res);
    setSending(false);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[#05070b]/80 p-4 backdrop-blur-sm animate-fadeIn sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${plan} engagement`}
    >
      <div
        className="my-4 grid w-full max-w-4xl overflow-hidden rounded-2xl border border-border bg-panel shadow-2xl animate-slideIn lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---- Value panel ---- */}
        <aside className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-[#0b1220] via-[#0d1526] to-[#070a12] p-7 text-white lg:flex">
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-30 blur-2xl"
            style={{ background: "radial-gradient(closest-side, hsl(var(--accent)), transparent)" }}
            aria-hidden
          />
          <div className="relative">
            <BrandLogo className="h-7 w-auto opacity-95 brightness-0 invert" alt="Ontoligent" />
            <div className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-medium tracking-wide text-white/80">
              <Building2 size={12} /> {plan === "enterprise" ? "Enterprise" : "Pro"} engagement
            </div>
            <h2 className="mt-4 text-[1.7rem] font-semibold leading-tight tracking-tight">
              Priced on the value<br />it creates — not seats.
            </h2>
            <p className="mt-3 text-[13px] leading-relaxed text-white/65">
              Production decision systems start with a short scoping conversation and an
              architecture review by a Redpill engineer. Tell us how you'll use Ontoligent
              and we'll confirm scope and a precise figure.
            </p>

            <ul className="mt-6 space-y-2.5">
              {planValue.map((v) => (
                <li key={v} className="flex gap-2.5 text-[12.5px] leading-snug text-white/85">
                  <Check size={15} className="mt-0.5 shrink-0 text-[hsl(var(--accent))]" />
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative mt-8 rounded-xl border border-white/10 bg-white/[0.04] p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/45">
              Typical {plan === "enterprise" ? "enterprise" : "Pro"} engagement
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {SCOPE.map((s) => (
                <span key={s} className="rounded-full border border-white/12 bg-white/[0.05] px-2 py-0.5 text-[10.5px] text-white/70">
                  {s}
                </span>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-white/45">
              <Lock size={11} /> Typically deployed in 8–16 weeks. A Redpill architect confirms scope and pricing.
            </div>
          </div>
        </aside>

        {/* ---- Form / success ---- */}
        <div className="relative max-h-[88vh] overflow-y-auto p-6 sm:p-7">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-border/40 hover:text-text"
            aria-label="Close"
          >
            <X size={16} />
          </button>

          {result?.ok ? (
            <div className="flex min-h-[360px] flex-col items-center justify-center text-center">
              <div className="grid h-14 w-14 place-items-center rounded-full border border-good/30 bg-good/10 text-good">
                <Check size={26} />
              </div>
              <h3 className="mt-4 text-lg font-semibold">Request received</h3>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted">
                {result.method === "backend"
                  ? "We've emailed you a confirmation and notified our team. A Redpill architect will reply within one business day."
                  : result.method === "emailjs"
                  ? "Your request has been sent. A Redpill architect will reply within one business day."
                  : "Your request is ready in your email client — send it and we'll reply within one business day."}
              </p>
              <button className="btn-primary mt-6" onClick={onClose}>Done</button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="lg:hidden">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
                  {plan === "enterprise" ? "Enterprise" : "Pro"} engagement
                </div>
                <h3 className="mt-1 text-lg font-semibold tracking-tight">Talk to our team</h3>
              </div>
              <div className="hidden lg:block">
                <h3 className="text-lg font-semibold tracking-tight">Tell us about your deployment</h3>
                <p className="mt-1 text-[12.5px] text-muted">
                  Share your scope and a Redpill architect will confirm the fit and pricing.
                  Every field is optional except your contact details.
                </p>
              </div>

              <SectionLabel>About you</SectionLabel>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Full name">
                  <input className={inputCls} required value={name} onChange={(e) => setName(e.target.value)} />
                </Field>
                <Field label="Work email">
                  <input className={inputCls} type="email" required value={email}
                         onChange={(e) => setEmail(e.target.value)} />
                </Field>
                <Field label="Company">
                  <input className={inputCls} value={company} onChange={(e) => setCompany(e.target.value)} />
                </Field>
                <Field label="Industry">
                  <select className={inputCls} value={ind} onChange={(e) => setInd(e.target.value)}>
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{i.replace(/_/g, " ")}</option>)}
                  </select>
                </Field>
              </div>

              <SectionLabel>The work</SectionLabel>
              <Field label="What decision or workflow will it run?">
                <textarea className={`${inputCls} min-h-[68px] resize-y`} value={useCase}
                          placeholder="e.g. auto-triage claims and surface fraud signals with citations"
                          onChange={(e) => setUseCase(e.target.value)} />
              </Field>

              <SectionLabel>Deployment</SectionLabel>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Hosting">
                  <div className="grid grid-cols-2 gap-2">
                    <Pick active={hosting === "redpill_hosted"} onClick={() => setHosting("redpill_hosted")}
                          Icon={Cloud} label="We host" />
                    <Pick active={hosting === "self_hosted"} onClick={() => setHosting("self_hosted")}
                          Icon={Server} label="Self-hosted" />
                  </div>
                </Field>
                <Field label="LLM">
                  <div className="grid grid-cols-2 gap-2">
                    <Pick active={llm === "managed"} onClick={() => setLlm("managed")}
                          Icon={ShieldCheck} label="Managed" />
                    <Pick active={llm === "byo"} onClick={() => setLlm("byo")}
                          Icon={KeyRound} label="Own key" />
                  </div>
                </Field>
                <Field label="Data volume">
                  <select className={inputCls} value={volume} onChange={(e) => setVolume(e.target.value as Volume)}>
                    {(Object.keys(VOLUME_LABEL) as Volume[]).map((v) => (
                      <option key={v} value={v}>{VOLUME_LABEL[v]}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Workflows in production">
                  <input className={inputCls} type="number" min={0} max={100} value={workflows}
                         onChange={(e) => setWorkflows(Number(e.target.value))} />
                </Field>
              </div>

              <SectionLabel>Compliance</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {COMPLIANCE.map((c) => (
                  <button key={c.key} type="button" onClick={() => toggleCompliance(c.key)}
                    className={`rounded-lg border px-2.5 py-1.5 text-[12px] transition ${
                      compliance.includes(c.key)
                        ? "border-accent bg-accent/10 text-accent"
                        : "border-border text-muted hover:border-accent/40 hover:text-text"
                    }`}>
                    {c.label}
                  </button>
                ))}
              </div>

              <SectionLabel>Timeline</SectionLabel>
              <select className={inputCls} value={timeline} onChange={(e) => setTimeline(e.target.value)}>
                <option value="this_quarter">This quarter</option>
                <option value="next_quarter">Next quarter</option>
                <option value="evaluating">Evaluating options</option>
              </select>

              <div className="mt-5 rounded-xl border border-border bg-bg/50 p-3.5">
                <div className="flex items-center gap-2 text-[12px] font-medium">
                  <UserCheck size={14} className="text-accent" /> Recommended next step
                </div>
                <p className="mt-1 text-[12px] leading-relaxed text-muted">{recommended}</p>
              </div>

              <button className="btn-primary mt-5 w-full justify-center py-2.5" type="submit" disabled={sending}>
                {sending
                  ? <><Loader2 size={15} className="animate-spin" /> Sending…</>
                  : <>Request {plan} engagement <ArrowRight size={15} /></>}
              </button>
              <p className="mt-2.5 flex items-center justify-center gap-1.5 text-[11px] text-muted">
                <Lock size={11} /> Confidential. We reply within one business day.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Pick({ active, onClick, Icon, label }: {
  active: boolean; onClick: () => void; Icon: typeof Cloud; label: string;
}) {
  return (
    <button type="button" onClick={onClick}
      className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-[12px] transition ${
        active
          ? "border-accent bg-accent/10 text-accent"
          : "border-border text-muted hover:border-accent/40 hover:text-text"
      }`}>
      <Icon size={14} /> {label}
    </button>
  );
}
